'use client'

import { createContext, useCallback, useContext, useState } from 'react'
import type { ISnackBarLevel, ISnackBarDuration } from '@/CommonComponents/SnackBar'
import type { ReactNode } from 'react'
import { SnackBar } from '@/CommonComponents'

// Define types for SnackbarContext and SnackbarProvider props
export interface ISnackbarContextType {
  (message: string, variant?: ISnackBarLevel, duration?: ISnackBarDuration): void
}

interface ISnackbarProviderProps {
  children: ReactNode
}

export interface ISnackbarState {
  show: boolean
  message: string
  variant: ISnackBarLevel
  duration: ISnackBarDuration
}

const snackbarContext = createContext<ISnackbarContextType | undefined>(undefined)

export function SnackbarProvider({ children }: ISnackbarProviderProps) {
  const [snackbar, setSnackbar] = useState<ISnackbarState>({
    show: false,
    message: '',
    variant: 'success',
    duration: 'medium',
  })

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, show: false }))
  }

  const showSnackbar = useCallback<ISnackbarContextType>((message, variant = 'success', duration = 'medium') => {
    setSnackbar({ show: true, message, variant, duration })
  }, [])

  return (
    <snackbarContext.Provider value={showSnackbar}>
      {children}
      <SnackBar
        message={snackbar.message}
        level={snackbar.variant}
        onClose={handleSnackbarClose}
        open={snackbar.show}
      />
    </snackbarContext.Provider>
  )
}

export const useSnackbar = (): ISnackbarContextType => {
  const context = useContext(snackbarContext)
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider')
  }
  return context
}
